import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";

const STEPS = [
  { key: "mapping", n: 1, label: "Map rooms to types", to: "/assets/settings" },
  { key: "templates", n: 2, label: "Confirm & activate templates", to: "/assets/settings" },
  { key: "opening", n: 3, label: "Allocate & finalize opening stock", to: "/assets/master" },
];

const TIPS = {
  mapping: (c) => `Use the dropdowns below to assign each room to Family Suite, Cottage, Suite or Deluxe. ${c.rooms_unmapped} room${c.rooms_unmapped === 1 ? "" : "s"} still need a type. Par calculations stay off until all rooms are mapped. Rooms stay in the existing room master — this only adds an asset room type.`,
  templates: (c) => `Quantities are prefilled as drafts and excluded from stock calculations until activated. Check every line, correct if needed, press Save, then Activate. ${c.templates_activated} of ${c.templates_total} templates are active. Activation needs every line quantity filled and at least one mapped room.`,
  opening: () => "Enter each item's opening quantity, then add allocation lines (Store and/or rooms) until every item shows \"Allocated X of X ✓\" — finalization is blocked otherwise. Finish with Finalize & Lock Opening.",
};

export default function SetupGuide({ current = null }) {
  const [c, setC] = useState(null);

  useEffect(() => { api.get("/assets/setup-checklist").then(({ data }) => setC(data)).catch(() => {}); }, []);

  if (!c || c.operational) return null;
  const doneMap = { mapping: c.mandatory?.room_mapping, templates: c.mandatory?.templates, opening: c.mandatory?.opening };
  const nextStep = STEPS.find(s => !doneMap[s.key]);

  return (
    <Card className="p-4 mb-4 border-sky-200 bg-sky-50" data-testid="setup-guide">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-sky-800">Setup guide</span>
        {STEPS.map(s => {
          const done = doneMap[s.key];
          const isCurrent = current === s.key;
          return (
            <span key={s.key} className={`inline-flex items-center gap-1.5 text-sm ${done ? "text-emerald-700" : isCurrent ? "text-sky-900 font-semibold" : "text-slate-500"}`} data-testid={`setup-step-${s.key}`}>
              {done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
              {s.n}. {s.label}
            </span>
          );
        })}
      </div>
      {current && TIPS[current] ? (
        <div className="text-sm text-sky-900" data-testid="setup-tip">{TIPS[current](c)}</div>
      ) : nextStep ? (
        <div className="text-sm text-sky-900" data-testid="setup-tip">
          Next: <strong>{nextStep.label}</strong>.{" "}
          <Link to={nextStep.to} className="underline inline-flex items-center gap-1">
            Go to step {nextStep.n} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : null}
    </Card>
  );
}
